import { FaCheck } from "react-icons/fa";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import * as yup from "yup";
import { FaTrashAlt } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";

import { InputLabel } from "components/atoms/Inputs";
import { PaziaButton, PaziaInput, PaziaToggleIcon } from "components/atoms";
import { useSurvey } from "pages/SurveyCreate/store/reducer";
import { questionBaseSchema } from "./BaseQuestionForm";
import { QuestionForm } from "./QuestionForm";
import { Question } from "survey";

const OTHER_ID = "-1";
interface SelectQuestionFormProps {
  question: Question;
}
export const SelectQuestionForm = ({ question }: SelectQuestionFormProps) => {
  const { dispatch } = useSurvey();
  const methods = useFormContext<SelectQuestionFormValues>();
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: QUESTION_SELECT_FORM_NAMES.OPTIONS,
    keyName: "key",
  });

  const { setValue, getValues } = methods;
  const onSwitchType = () => {
    fields.forEach((field, index) => {
      setValue(`${QUESTION_SELECT_FORM_NAMES.OPTIONS}.${index}.isCorrect`, false);
    });
    dispatch({
      type: "UPDATE_QUESTION",
      payload: {
        ...question,
        inputType: question.inputType === "multi-select" ? "single-select" : "multi-select",
        options: question.options?.map((option) => ({ ...option, isCorrect: false })),
      },
    });
  };

  const toggleIsCorrect = (index: number) => {
    const isSingleSelect = question.inputType === "single-select";

    if (isSingleSelect) {
      const newOptions = question.options?.map((option, i) => {
        if (i === index) {
          return { ...option, isCorrect: true };
        }
        setValue(`${QUESTION_SELECT_FORM_NAMES.OPTIONS}.${i}.isCorrect`, false);
        return { ...option, isCorrect: false };
      });

      dispatch({
        type: "UPDATE_QUESTION",
        payload: {
          ...question,
          options: newOptions,
        },
      });
      return;
    }

    dispatch({
      type: "UPDATE_QUESTION",
      payload: {
        ...question,
        options: question.options?.map((option, i) =>
          i === index ? { ...option, isCorrect: !option.isCorrect } : option
        ),
      },
    });
  };

  const onOptionChange = () => {
    const values = getValues(QUESTION_SELECT_FORM_NAMES.OPTIONS);
    dispatch({
      type: "UPDATE_QUESTION",
      payload: {
        ...question,
        options: values,
      },
    });
  };
  const onAddOther = () => {
    if (fields.some((field) => field.id === OTHER_ID)) return;
    append({ value: "Inne", id: OTHER_ID, isCorrect: false });
    onOptionChange();
  };

  return (
    <QuestionForm question={question}>
      <InputLabel name="options" label="Opcje wyboru" />
      {fields.map((field, index) => (
        <div key={field.key} className="flex items-center mt-2 gap-2">
          {OTHER_ID !== field.id && (
            <PaziaToggleIcon
              name={`${QUESTION_SELECT_FORM_NAMES.OPTIONS}[${index}].isCorrect`}
              onToggle={() => toggleIsCorrect(index)}
            >
              <FaCheck />
            </PaziaToggleIcon>
          )}
          <PaziaInput
            name={`${QUESTION_SELECT_FORM_NAMES.OPTIONS}[${index}].value`}
            className="w-full"
            onChange={onOptionChange}
            disabled={field.id === OTHER_ID}
            inputClassName={field.id === OTHER_ID ? "bg-slate-50" : ""}
          />
          <FaTrashAlt
            onClick={() => {
              remove(index);
              onOptionChange();
            }}
            size={12}
            className={iconClass}
          />
          <FaPlus onClick={() => append({ value: "", id: uuidv4(), isCorrect: false })} className={iconClass} />
        </div>
      ))}

      <div className="mt-4 gap-4 flex flex-wrap text-nowrap">
        <PaziaButton
          btnType="button"
          title={`Dodaj "Inne"`}
          type="tertiary"
          onClick={onAddOther}
          disabled={fields.some((field) => field.id === OTHER_ID)}
        />
        <PaziaButton
          btnType="button"
          title={question.inputType === "multi-select" ? `Zmień na Pojedyńczy wybór` : "Zmień na Wielokrotny wybór"}
          type="tertiary"
          onClick={onSwitchType}
        />
      </div>
    </QuestionForm>
  );
};

const iconClass = "text-slate-400 hover:text-black cursor-pointer dark:hover:text-slate-200";
export const QUESTION_SELECT_FORM_NAMES = {
  OPTIONS: "options",
} as const;

const questionSelectFromSchema = yup
  .object({
    [QUESTION_SELECT_FORM_NAMES.OPTIONS]: yup
      .array()
      .of(
        yup.object().shape({
          value: yup.string().required(),
          id: yup.string().required(),
          isCorrect: yup.boolean().default(false),
        })
      )
      .required(),
  })
  .required();

export const selectQuestionSchema = questionSelectFromSchema.concat(questionBaseSchema);
export type SelectQuestionFormValues = yup.InferType<typeof selectQuestionSchema>;
